<template>
  <div>
    <BaseTable
      :rows="rows"
      :columns="columns"
      idKey="id"
      :searchable="false"
      :selectable="false"
      :loading="loading"
      :pagination="pagination"
      @changePage="onPageChange"
      @updatePageSize="onPageSizeChange"
    >
      <template #buttons>
        <select class="form-select form-select-sm" style="width:auto;" v-model="statusFilter" @change="fetchLogs(1)">
          <option value="">All Statuses</option>
          <option value="queued">Queued</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
          <option value="bounced">Bounced</option>
          <option value="complaint">Complaint</option>
          <option value="failed">Failed</option>
        </select>
      </template>

      <template #col-organization="{ row }">
        {{ row.organization?.name || '—' }}
      </template>
      <template #col-status="{ row }">
        <span class="badge" :class="statusBadgeClass(row.status)" style="font-size:11px;">{{ row.status }}</span>
      </template>
      <template #col-latency="{ row }">
        {{ row.delivery_latency_ms != null ? `${row.delivery_latency_ms} ms` : '—' }}
      </template>
      <template #col-sent_at="{ row }">{{ formatDate(row.sent_at) }}</template>
      <template #col-delivered_at="{ row }">{{ formatDate(row.delivered_at) }}</template>
      <template #col-opened_at="{ row }">{{ formatDate(row.opened_at) }}</template>
      <template #col-clicked_at="{ row }">{{ formatDate(row.clicked_at) }}</template>
      <template #col-actions="{ row }">
        <button class="btn btn-sm btn-outline-primary" @click.prevent="previewMessage(row)">Preview</button>
      </template>
    </BaseTable>

    <!-- Preview modal layered on top -->
    <div v-if="showPreview" class="modal-backdrop" style="position:fixed;inset:0;z-index:2000;display:flex;align-items:center;justify-content:center;">
      <div class="card" style="width:80%;height:80%;overflow:auto;">
        <div class="card-header d-flex justify-content-between align-items-center">
          <strong>Email Preview</strong>
          <div>
            <button class="btn btn-sm btn-secondary me-2" @click="downloadMessage">Download</button>
            <button class="btn btn-sm btn-danger" @click="closePreview">Close</button>
          </div>
        </div>
        <div class="card-body p-0" style="height:calc(100% - 56px);">
          <div v-if="previewLoading" class="p-3">Loading...</div>
          <div v-else v-html="previewHtml"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import BaseTable from '@/components/Table/Table.vue'
import api from '@/config/api'

const loading = ref(true)
const rows = ref([])
const statusFilter = ref('')

const pagination = ref({
  currentPage: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasPrevious: false,
  hasNext: false,
})

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'recipient_email', label: 'RECIPIENT' },
  { key: 'organization', label: 'ORGANIZATION' },
  { key: 'status', label: 'STATUS' },
  { key: 'sent_at', label: 'SENT' },
  { key: 'delivered_at', label: 'DELIVERED' },
  { key: 'opened_at', label: 'OPENED' },
  { key: 'clicked_at', label: 'CLICKED' },
  { key: 'latency', label: 'LATENCY' },
  { key: 'actions', label: '' },
]

function statusBadgeClass(status) {
  return {
    'bg-gradient-secondary': ['queued', 'sent'].includes(status),
    'bg-gradient-success': ['delivered', 'opened', 'clicked'].includes(status),
    'bg-gradient-danger': ['bounced', 'complaint', 'failed'].includes(status),
  }
}

function formatDate(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

async function fetchLogs(page = 1, pageSize = pagination.value.pageSize) {
  loading.value = true
  try {
    const response = await api.get('/admin/lead-logs', {
      params: {
        page_number: page,
        page_size: pageSize,
        status: statusFilter.value,
      },
    })
    rows.value = response.data?.output || []
    const pag = response.data?.pagination || {}
    pagination.value = {
      currentPage: pag.current_page || 1,
      pageSize: pag.page_size || pageSize,
      totalRecords: pag.total_records || 0,
      totalPages: pag.total_pages || 0,
      hasPrevious: pag.has_previous || false,
      hasNext: pag.has_next || false,
    }
  } catch (e) {
    console.error('Error fetching lead email logs:', e)
    rows.value = []
  } finally {
    loading.value = false
  }
}

function onPageChange(page) { fetchLogs(page) }
function onPageSizeChange(size) { fetchLogs(1, size) }

// Preview modal state
const showPreview = ref(false)
const previewHtml = ref('')
const previewLoading = ref(false)
let previewLogId = null

async function previewMessage(row) {
  previewLogId = row.id
  showPreview.value = true
  previewLoading.value = true
  previewHtml.value = ''
  try {
    const resp = await api.get(`/admin/lead-logs/${row.id}/message`, { responseType: 'text' })
    previewHtml.value = resp.data
  } catch (e) {
    previewHtml.value = `<div class="p-3">Failed to load message preview: ${e?.message || e}</div>`
  } finally {
    previewLoading.value = false
  }
}

function closePreview() {
  showPreview.value = false
  previewHtml.value = ''
  previewLogId = null
}

function downloadMessage() {
  if (!previewLogId) return
  const url = `${api.defaults.baseURL}/admin/lead-logs/${previewLogId}/message`
  window.open(url, '_blank')
}

onMounted(() => fetchLogs())
</script>
